import angular from 'angular';
import '../../shared.module';
import template from './order-create-modal.component.html';
import './order-create-modal.component.scss';
import { CHARGE_STATUS_LABELS, FULFILLMENT_LABELS } from '../order-status.constant';
import {
  cartErrors,
  cartRequest,
  chargeRequest,
  emptyCartLine,
  isSeatedLine,
  missingSession,
  orderRequest,
  selectedSeats
} from '../order.helper';
import {
  accessibleSeatNotice,
  isAccessibleSeat,
  seatKindLabel,
  seatSurchargeLabel,
  surchargeTotalCents
} from '../seat-map/seat-map.helper';
import { toSelectOptions } from '../../helpers/select-options.helper';

function newIdempotencyKey($window) {
  const crypto = $window.crypto;
  if (crypto && crypto.randomUUID) return `painel:${crypto.randomUUID()}`;
  return `painel:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

export const CHARGE_METHOD_OPTIONS = [
  { value: 'pix', label: 'PIX (QR code e copia e cola)' },
  { value: 'payment_link', label: 'Link de pagamento' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'credit_card', label: 'Cartão de crédito (link)' }
];

const QUOTE_DELAY_MS = 400;

export function seatConflictMessage(labels) {
  if (!labels.length) return '';
  return labels.length === 1
    ? `O lugar ${labels[0]} acabou de ser ocupado. Escolha outro assento.`
    : `Os lugares ${labels.join(', ')} acabaram de ser ocupados. Escolha outros assentos.`;
}

function emptyCart(contactId) {
  return {
    contatoId: contactId || '',
    canal: '',
    formaEntrega: '',
    endereco: '',
    observacoes: '',
    linhas: []
  };
}

class OrderCreateModalController {
  constructor(OrderApiService, SeatMapApiService, NotificationService, $window, $timeout, $q) {
    this.$timeout = $timeout;
    this.$q = $q;
    this.OrderApiService = OrderApiService;
    this.SeatMapApiService = SeatMapApiService;
    this.NotificationService = NotificationService;
    this.$window = $window;
    this.chargeMethods = CHARGE_METHOD_OPTIONS;
    this.chargeLabels = CHARGE_STATUS_LABELS;
    this.fulfillmentOptions = toSelectOptions(FULFILLMENT_LABELS);
    this.reset();
  }

  reset() {
    this.cart = emptyCart(this.contactId);
    this.step = 'cart';
    this.order = null;
    this.charge = null;
    this.charging = false;
    this.sending = false;
    this.sent = false;
    this.payment = { metodo: 'pix', documento: '', email: '' };
    this.paymentErrors = {};
    this.errors = {};
    this.quote = null;
    this.quoting = false;
    this.quoteError = '';
    this.saving = false;
    this.contactLabel = '';
    this.contactOptions = [];
    this.productOptions = [];
    this.channelOptions = [];
    this.tariffOptions = [];
    this.seatLine = null;
    this.seatNotice = '';
    this.seatWarning = '';
    this.idempotencyKey = newIdempotencyKey(this.$window);
  }

  $onChanges(changes) {
    if (changes.isOpen && this.isOpen) {
      this.reset();
      if (!this.contactId) this.searchContacts();
      this.searchProducts();
      this.OrderApiService.lookups().then(
        ([channels, tariffs]) => {
          this.channelOptions = channels;
          this.tariffOptions = tariffs;
          if (this.conversationId && channels.some((channel) => channel.value === 'whatsapp')) {
            this.cart.canal = 'whatsapp';
          }
        },
        (failure) => this.NotificationService.error(failure.message)
      );
    }
  }

  searchContacts(text) {
    return this.OrderApiService.searchContacts(text || '').then(
      (options) => {
        this.contactOptions = options;
      },
      (failure) => this.NotificationService.error(failure.message)
    );
  }

  pickContact(option) {
    this.cart.contatoId = option.value;
    this.contactLabel = option.label;
    this.errors = { ...this.errors, contato: undefined };
  }

  addProduct(option) {
    const empty = this.cart.linhas.find((line) => !line.produtoId);
    const line = empty || emptyCartLine();
    if (!empty) this.cart.linhas.push(line);
    line.produtoId = option.value;
    return this.selectProduct(line);
  }

  searchProducts(text) {
    return this.OrderApiService.searchProducts(text || '').then(
      (options) => {
        this.productOptions = options;
      },
      (failure) => this.NotificationService.error(failure.message)
    );
  }

  changed() {
    this.scheduleQuote();
  }

  selectProduct(line) {
    Object.assign(line, {
      ...emptyCartLine(),
      produtoId: line.produtoId,
      quantidade: line.quantidade
    });
    this.changed();
    if (!line.produtoId) return null;
    return this.OrderApiService.productChoice(line.produtoId).then(
      (detail) => {
        line.detalhe = detail;
        this.scheduleQuote();
      },
      (failure) => this.NotificationService.error(failure.message)
    );
  }

  offersFor(line) {
    if (!line.detalhe) return [];
    return line.detalhe.ofertas.filter(
      (offer) => !offer.varianteId || offer.varianteId === line.varianteId
    );
  }

  sessionChanged(line) {
    Object.assign(line, {
      lugares: null,
      assentosIds: [],
      assentosErro: '',
      carregandoAssentos: Boolean(line.sessaoId)
    });
    this.changed();
    if (!line.sessaoId) return null;
    return this.loadSeats(line).then((seating) => {
      if (seating && isSeatedLine(line)) this.openSeats(line);
      return seating;
    });
  }

  loadSeats(line) {
    const sessionId = line.sessaoId;
    line.carregandoAssentos = true;
    return this.SeatMapApiService.sessionSeats(sessionId).then(
      (seating) => {
        if (line.sessaoId !== sessionId) return null;
        line.carregandoAssentos = false;
        line.lugares = seating.assentos.length ? seating : null;
        if (line.lugares) line.quantidade = line.assentosIds.length;
        this.scheduleQuote();
        return seating;
      },
      (failure) => {
        if (line.sessaoId !== sessionId) return null;
        line.carregandoAssentos = false;
        line.assentosErro = failure.message;
        this.scheduleQuote();
        return null;
      }
    );
  }

  isSeated(line) {
    return isSeatedLine(line);
  }

  openSeats(line) {
    this.seatLine = line;
    this.seatNotice = selectedSeats(line).some(isAccessibleSeat) ? this.seatNotice : '';
    this.step = 'seats';
  }

  closeSeats() {
    this.seatLine = null;
    this.step = 'cart';
  }

  toggleSeat(seat, selected) {
    const line = this.seatLine;
    if (!line) return;
    const ids = line.assentosIds.filter((id) => id !== seat.id);
    line.assentosIds = selected ? [...ids, seat.id] : ids;
    line.quantidade = line.assentosIds.length;
    this.seatWarning = '';
    if (selected && isAccessibleSeat(seat)) {
      this.seatNotice = accessibleSeatNotice(seat);
      this.NotificationService.warning(this.seatNotice);
    } else if (!selectedSeats(line).some(isAccessibleSeat)) {
      this.seatNotice = '';
    }
    this.changed();
  }

  chosenSeats(line) {
    return selectedSeats(line);
  }

  seatDescription(seat) {
    return [seat.tipo === 'standard' ? '' : seatKindLabel(seat), seatSurchargeLabel(seat)]
      .filter(Boolean)
      .join(' · ');
  }

  surchargeTotal(line) {
    return surchargeTotalCents(selectedSeats(line)) / 100;
  }

  quoteLineFor(line) {
    if (!this.quote) return null;
    const index = this.cart.linhas.filter((item) => item.produtoId).indexOf(line);
    return index >= 0 ? this.quote.lines[index] || null : null;
  }

  sessionWhen(line) {
    const session = line && line.lugares && line.lugares.sessao;
    if (!session || !session.inicio) return '';
    return new Date(session.inicio).toLocaleString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  isSeatConflict(failure) {
    return Boolean(failure && failure.reason === 'SEAT_UNAVAILABLE');
  }

  reloadSeats(line) {
    const previous = line.lugares;
    const chosen = [...line.assentosIds];
    return this.SeatMapApiService.sessionSeats(line.sessaoId).then(
      (seating) => {
        const available = new Set(
          seating.assentos.filter((seat) => seat.status === 'available').map((seat) => seat.id)
        );
        const lost = chosen.filter((id) => !available.has(id));
        line.lugares = seating.assentos.length ? seating : null;
        line.assentosIds = chosen.filter((id) => available.has(id));
        line.quantidade = line.assentosIds.length;
        return lost.map((id) => {
          const seat = previous.assentos.find((item) => item.id === id);
          return seat ? seat.rotulo : id;
        });
      },
      () => []
    );
  }

  handleSeatConflict(failure) {
    const lines = this.cart.linhas.filter(isSeatedLine);
    this.idempotencyKey = newIdempotencyKey(this.$window);
    return this.$q.all(lines.map((line) => this.reloadSeats(line))).then((lostByLine) => {
      const labels = lostByLine.flat();
      this.seatWarning = seatConflictMessage(labels) || failure.message;
      this.NotificationService.warning(this.seatWarning);
      const affected = lines.find((line, index) => lostByLine[index].length);
      if (affected) this.openSeats(affected);
      this.scheduleQuote();
      return labels;
    });
  }

  rulesLabel(line) {
    return line.pricing.appliedRules.map((rule) => rule.name).join(', ');
  }

  removeLine(index) {
    this.cart.linhas.splice(index, 1);
    this.changed();
  }

  $onDestroy() {
    if (this.quoteTimer) this.$timeout.cancel(this.quoteTimer);
  }

  scheduleQuote() {
    if (this.quoteTimer) this.$timeout.cancel(this.quoteTimer);
    this.quoteError = '';
    const items = cartRequest(this.cart).items;
    const pending = this.cart.linhas.some(
      (line) =>
        !line.detalhe ||
        line.carregandoAssentos ||
        missingSession(line) ||
        (isSeatedLine(line) && !line.assentosIds.length)
    );
    if (!items.length || pending) {
      this.quote = null;
      return;
    }
    this.quoteTimer = this.$timeout(() => this.calculate(), QUOTE_DELAY_MS);
  }

  calculate() {
    this.quoteTimer = null;
    const sequence = (this.quoteSequence || 0) + 1;
    this.quoteSequence = sequence;
    this.quoting = true;
    return this.OrderApiService.quote(cartRequest(this.cart)).then(
      (quote) => {
        if (sequence !== this.quoteSequence) return;
        this.quoting = false;
        this.quote = quote;
      },
      (failure) => {
        if (sequence !== this.quoteSequence) return null;
        this.quoting = false;
        this.quote = null;
        this.quoteError = failure.message;
        return this.isSeatConflict(failure) ? this.handleSeatConflict(failure) : null;
      }
    );
  }

  submit() {
    if (this.saving) return null;
    this.errors = cartErrors(this.cart);
    if (Object.keys(this.errors).length) return null;
    if (!this.quote || !this.quote.available) {
      this.NotificationService.error(
        'Complete os itens do carrinho: o total precisa estar calculado e com disponibilidade.'
      );
      return null;
    }
    this.saving = true;
    return this.OrderApiService.create(
      orderRequest(this.cart, this.idempotencyKey, this.conversationId)
    ).then(
      (order) => {
        this.saving = false;
        this.NotificationService.success(
          `Pedido #${order.numero} criado. Vagas e estoque reservados.`
        );
        this.order = order;
        this.step = 'charge';
      },
      (failure) => {
        this.saving = false;
        this.quote = null;
        if (this.isSeatConflict(failure)) return this.handleSeatConflict(failure);
        this.NotificationService.error(failure.message);
        return null;
      }
    );
  }

  generateCharge() {
    if (this.charging || !this.order) return null;
    const document = (this.payment.documento || '').replace(/\D/g, '');
    this.paymentErrors =
      document.length === 11 || document.length === 14
        ? {}
        : { documento: 'Informe um CPF ou CNPJ válido.' };
    if (Object.keys(this.paymentErrors).length) return null;
    this.charging = true;
    return this.OrderApiService.createCharge(
      this.order.id,
      chargeRequest(this.order.id, this.payment)
    ).then(
      (charge) => {
        this.charging = false;
        this.charge = charge;
        if (charge.status === 'failed')
          this.NotificationService.error(charge.falha || 'O provedor recusou a cobrança.');
      },
      (failure) => {
        this.charging = false;
        this.NotificationService.error(failure.message);
      }
    );
  }

  markPaid(order) {
    this.order = order;
    this.charge = { ...this.charge, status: 'paid' };
  }

  copy(text) {
    const clipboard = this.$window.navigator.clipboard;
    if (!clipboard || !text) return null;
    return clipboard.writeText(text).then(
      () => this.NotificationService.success('Copiado.'),
      () => this.NotificationService.error('Não foi possível copiar.')
    );
  }

  sendToConversation() {
    if (this.sending || !this.charge || !this.conversationId) return null;
    this.sending = true;
    return this.OrderApiService.sendChargeToConversation(this.conversationId, this.charge.id).then(
      () => {
        this.sending = false;
        this.sent = true;
        this.NotificationService.success('Cobrança enviada ao cliente na conversa.');
      },
      (failure) => {
        this.sending = false;
        this.NotificationService.error(failure.message);
      }
    );
  }

  finish() {
    if (this.order && this.onCreated) this.onCreated({ order: this.order });
    else if (this.onClose) this.onClose();
  }

  close() {
    if (this.saving || this.charging || this.sending) return;
    if (this.step === 'seats') {
      this.closeSeats();
      return;
    }
    if (this.order) this.finish();
    else if (this.onClose) this.onClose();
  }
}

OrderCreateModalController.$inject = [
  'OrderApiService',
  'SeatMapApiService',
  'NotificationService',
  '$window',
  '$timeout',
  '$q'
];

angular.module('app.shared').component('cpOrderCreateModal', {
  bindings: {
    isOpen: '<',
    contactId: '<',
    contactName: '<',
    conversationId: '<',
    onClose: '&',
    onCreated: '&'
  },
  controller: OrderCreateModalController,
  controllerAs: 'vm',
  template
});
